/**
 * Created by rjulius on 2/9/16.
 */
import OperationViewModelMixin from "common/framework/OperationMixin";
import formOps from "common/datasources/form/formOperations";
import Model from "common/models/form/RenderedFormModel";
import SavedFormModel from "common/models/form/SavedFormModel";
import topic from "common/managers/topic";
import locale from "common/managers/locale";
import error from "common/managers/error";
import PromptInputType from "common/enums/PromptInputType";
import DataType from "common/enums/DataType";
import FormNewFileUploadModel from "common/models/form/FormNewFileUploadModel";
import "common/widgets/locale/Locale";
import FormActionType from "common/enums/form/FormActionType";
import themeOperations from "common/datasources/themes/themeOperations";

/**
 * This is a mixin that is added to FormsViewModel and ExternalFormsViewModel.
 * It is used for the View and Edit modes of Forms.
 */
export default $.extend({

    // whether the form is in view or edit mode
    readOnly: false,

    // whether the form should be displayed in responsive or absolute mode
    responsive: false,

    // FormInstanceDTO being viewed or edited
    displayedForm: undefined,

    // rendered form; full info to draw the form, controls, and steps, returned from renderForm()
    renderedForm: false,

    // FormStepModel of current step, from renderedForm
    currentStep: false,

    // whether there was an error rendering the form; if so, displays the message to show the user
    renderError: false,

    // binding for preloader when loading and rendering the form
    loadingForm: true,

    // binding for whether the form has unsaved changes from the user
    formDirty: false,

    // to have a link displayed as part of the error, set this to true.
    // when clicked, a renderErrorClick event will be triggered. If the message for this link
    // ever needs to change, it will need to be set with a new property here as well.
    // For now, the message is statically defined in the View.
    showLinkOnError: false,

    // If true, hides the word View || Edit in the header, hiding the context in which you are viewing or editing the form.
    // Used for external forms mode.
    hideContext: false,

    /**
     * Set a success message to be displayed to the user after submit. Handles the possibility of multiple display feedback submit actions, and resets
     * right before a new form is submitted.
     */
    successMessage: "",

    /** optionally used by extensions of this class to display an error message ALONG WITH a form. **/
    errorMessage: "",

    // mode for displaying success message ALONG WITH the form
    showSuccess: false,

    // reference binding for notification
    notify: undefined,

    // object of all the uploads/removes for the upload controls on the form
    pendingUploads: undefined,

    // array of completed file uploads
    completedUploads: [],

    // cache the values that are submitted on a form so values are retained when a form or step are loaded
    // the key is the form control name and the value is the formData for that form control, the value is an array to support lists
    _cachedData: {},

    headerText: function() {
        if (this.get("readOnly")) {
            return locale.get("forms.view", this.get("displayedForm.formName"), this.get("currentStep.name"));
        } else {
            return locale.get("forms.edit", this.get("displayedForm.formName"), this.get("currentStep.name"));
        }
    },

    setResponsive: function(r) {
        this.set("responsive", r);
    },

    isResponsive: function() {
        return !!this.get("responsive");
    },

    updateHeader: $.noop,

    initDisplay: function() {
        this.bind("change", $.proxy(this.onDisplayChange, this));
        this.clearCache();
    },

    onDisplayChange: function(e) {
        if (e.field == "renderError" && this.get("renderError")) {
            this.set("displayedForm", null);
            this.set("renderedForm", false);
            this.set("currentStep", false);
            this.set("formDirty", false);
        }
    },

    /**
     * Clears the cached form data for rendering forms with prepopulated data
     */
    clearCache: function() {
        this._cachedData = {};
    },

    /**
     * View a form
     * @param form the form to view
     * @param forceMessage If forced into view mode, show a warning with this message
     * @returns {*}
     */
    viewForm: function(form, forceMessage) {
        this.clearCache();
        return this._viewForm(form, forceMessage);
    },

    _viewForm: function(form, forceMessage) {
        var me = this;
        return this._openForm(form, "VIEW").done(this.ifOp(function() {
            me.openComplete(true);
            if (forceMessage) {
                me.notify().warning(locale.get(forceMessage));
            }
        }));
    },

    editForm: function(form) {
        this.clearCache();
        return this._editForm(form);
    },

    _editForm: function(form) {
        var me = this;
        return this._openForm(form, "EDIT").done(this.ifOp(function() {
            me.openComplete(false);
        }));
    },

    // set readOnly flag, and set the header of the form container (in the form panel, that is the panel title)
    openComplete: function(isReadOnly) {
        this.set("readOnly", isReadOnly);
        this.updateHeader(this.headerText());
    },

    isReadonly() {
        return this.get("readOnly")
            || !!this.isActionInProgress && this.isActionInProgress();
    },

    formContainer: undefined,

    _getFormModel: function(data) {
        if (data instanceof SavedFormModel) {
            return data;
        }
        var form = new SavedFormModel();
        for (var prop in data) {
            if (data.hasOwnProperty(prop)) {
                form[prop] = data[prop];
            }
        }
        return form;
    },

    /**
     * Add a value to the formData cache
     * @param key {string} The name of the form control
     * @param value {{dataType: string, formControlName: string, ...}[]} The formData to cache, value will be stringValue, booleanValue, or etc.
     * @private
     */
    _cacheFormData: function(key, value) {
        this._cachedData[key] = value;
    },

    /**
     * Remove a cached form data item for a form control that doesn't have a value
     * @param key {string} Name of the form control
     * @private
     */
    _removeCacheItem: function(key) {
        if (this._cachedData.hasOwnProperty(key)) {
            delete this._cachedData[key];
        }
    },

    /**
     * Get the data to use for rendering the form, this merges the data passed and the data that is retained from previous submissions
     * We have to support storage of string array data so we preprocess the formData to join values by form control name
     * @param formData {Array} The form formData array
     * @returns {Array} The data to render to the initial state of the form
     * @private
     */
    _getRenderData: function(formData) {
        var me = this;
        // process form data to combine array values by form control name
        var merge = {};
        formData.forEach(function(data) {
            if (!merge[data.formControlName]) {
                merge[data.formControlName] = [];
            }
            merge[data.formControlName].push(me._getFormDataItemForCache(data.dataType, data.formControlName, data[me._getFormDataValueProperty(data.dataType)]));
        });
        // merge into cache
        var prop;
        for (prop in merge) {
            this._cacheFormData(prop, merge[prop]);
        }
        var render = [];
        for (prop in this._cachedData) {
            this._cachedData[prop].forEach(function(cached) {
                render.push(cached);
            });
        }
        return render;
    },

    /**
     * Get the correct property name for the form data value based on data type
     * @param dataType {string} The data type of the form data value
     * @returns {string} The name of the value property
     * @private
     */
    _getFormDataValueProperty: function(dataType) {
        var valueProp = "stringValue";
        if (dataType === DataType.BOOLEAN) {
            valueProp = "booleanValue";
        } else if (dataType === DataType.DATE) {
            valueProp = "dateValue";
        } else if (dataType === DataType.INTEGER) {
            valueProp = "integerValue";
        } else if (dataType === DataType.DECIMAL) {
            valueProp = "decimalValue";
        }
        return valueProp;
    },

    /**
     * Get a form data item for storage into the local cache
     * @param dataType {string} The data type of the form control
     * @param formControlName {string} The name of the form control
     * @param value {string} The value of the form data item
     * @returns {{dataType: string, formControlName: string, ...}} The formData for the cache, value will be stringValue, booleanValue, or etc.
     * @private
     */
    _getFormDataItemForCache: function(dataType, formControlName, value) {
        var valueProp = this._getFormDataValueProperty(dataType);
        var formDataItem = {
            dataType: dataType,
            formControlName: formControlName
        };
        formDataItem[valueProp] = value;
        return formDataItem;
    },

    /**
     * Get a form data item to be sent to the server to be saved
     * @param dataType {string} The data type of the form control
     * @param formControlId {string} The id of the form control
     * @param formControlName {string} The name of the form control
     * @param value {string} The value of the form data item
     * @returns {{dataType: string, formControlId: string, formControlName: string, ...}} The formData to be saved, value will be stringValue, booleanValue, or etc.
     * @private
     */
    _getFormDataItemForSave: function(dataType, formControlId, formControlName, value) {
        var formDataItem = this._getFormDataItemForCache(dataType, formControlName, value);
        formDataItem.formControlId = formControlId;
        return formDataItem;
    },

    /**
     * Expects form to have formName, formId, id, or docId
     * @param form
     * @param mode VIEW or EDIT
     */
    _openForm: function(form, mode) {
        var me = this,
            renderData = JSON.parse(JSON.stringify((form || {}).formData || []));
        me.notify().hide();
        me.set("formDirty", false);
        me.set("renderError", false);
        me.set("loadingForm", true);

        form = this._getFormModel(form);

        var promise = $.Deferred().resolve().promise();
        // If the form to be viewed is a document, get the formInstance of
        // the document for rendering.
        if (form.docId) {
            var documentId = form.docId,
                disableEdit = form.disableEdit;
            // Call viewFormDocument / editFormDocument THEN render
            promise = formOps.getFormDocument(documentId, mode)
                .done(this.ifOp(function(data) {
                    form.accept(data);
                    form.docId = documentId;
                    form.disableEdit = disableEdit;
                }))
                .handle("error/message/SEC004", this.ifOp(function() {
                    if (me.get("displayedForm") && me.get("displayedForm").get("docId") == documentId) {
                        // If the security error is on the same form that's open, leave it in its current state
                        // and display the error as a notification.
                        topic.publish("app/notify/custom", "main.forms.error." + mode.toLowerCase(), "error");
                    } else {
                        // Otherwise, display the error inline on the Display Area.
                        me.set("renderError", locale.get("main.forms.error." + mode.toLowerCase()));
                    }
                }))
                .fail(this.ifOp(function() {
                    me.set("loadingForm", false);
                }));
        } else if (form.id) {
            var disableEdit = form.disableEdit;
            promise = formOps.getFormInstance(form.id)
                .done(this.ifOp(function(data) {
                    form.accept(data);
                    form.disableEdit = disableEdit;
                }))
                .fail(this.ifOp(function() {
                    me.set("loadingForm", false);
                }));
        }
        return promise.then(this.ifOp(function() {
            if (form.completed && mode == "EDIT") {
                return me._viewForm(form, "main.forms.error.completed");
            }

            me.set("displayedForm", form);
            me.set("renderedForm", false);
            me.set("currentStep", false);

            // every time we render, set a new formSessionId.
            // this is used by the file upload control
            var sessionId = kendo.guid().replace(new RegExp("-", "g"), "");
            me.set("formSessionId", sessionId);

            // reset the set of pending uploads and completed uploads
            me.set("pendingUploads", {});
            me.set("completedUploads", []);

            // construct the form object that we send for render so it contains the data passed from the user
            // and the retained values from previous submissions
            var formRender = JSON.parse(JSON.stringify(form));
            formRender.formData = this._getRenderData(renderData);
            return this.renderForm(formRender, mode, sessionId)
                .handle("error/message", this.ifOp(function(data) {
                    me.set("renderError", data.message);
                    // need to trigger also, for external forms to catch and auto-logout
                    me.trigger("forms/render/error", data.message);
                }))
                .then(this.ifOp(function(data) {

                    //Configure responsive mode
                    if (data.responsive === true) {
                        this.setResponsive({
                            spaceElements: data.spaceElements
                        });
                    } else {
                        this.setResponsive(false);
                    }

                    // Set form on Model
                    me.set("renderedForm", new Model());
                    me.renderedForm.accept(data);

                    // update formId on displayedForm, if not set
                    // Not set from New Forms list, since we don't know the active version, and not set
                    // from certain URL API calls
                    if (!me.displayedForm.formId) {
                        me.displayedForm.formId = me.get("renderedForm.id");
                    }
                    // formName not set from all URL API calls, so update it if missing
                    if (!me.displayedForm.formName) {
                        me.displayedForm.set("formName", me.get("renderedForm.name"));
                    }
                    // form data that is not on any step of this form needs to be deleted.
                    // this can include form data from the URL API, or an earlier form that loaded this
                    // form via a submit action, for example.
                    // create a list of form control names from all steps.
                    var formControlNames = [];
                    me.renderedForm.steps.forEach(function(step) {
                        if (step.controls) {
                            formControlNames = formControlNames.concat(step.controls.map(function(obj) {
                                return obj.name;
                            }));
                        }
                    });
                    // include only formData that is in at least one step of the form. If there isn't any,
                    // then there is none to remove.
                    if (me.displayedForm.formData) {
                        me.displayedForm.formData = me.displayedForm.formData.filter(function(obj) {
                            return formControlNames.indexOf(obj.formControlName) > -1;
                        });
                    }

                    this.setCurrentStep();
                    return this.setTheme(data.themeId);
                }))
                .always(this.ifOp(function() {
                    me.set("loadingForm", false);
                    this.focusForm();
                }));
        }));
    },

    setTheme(id) {
        return $.when().then(() => {
            if(id) {
                return themeOperations.getThemeProperties(id);
            }
        }).done(this.ifOp((properties) => {
            this.set("theme", properties);
        })).fail(this.ifOp(() => {
            this.set("theme", undefined);
        }));
    },

    /**
     * Focus the form container
     */
    focusForm: function() {
        this.formContainer().focus();
    },

    /**
     * Expose the render form function so it can be overridden
     * @param form
     * @param mode
     * @param sessionId
     * @returns {*}
     */
    renderForm: function(form, mode, sessionId) {
        return formOps.renderForm(form, mode, sessionId);
    },

    /**
     * Expose the set current step logic so it can be overridden
     */
    setCurrentStep: function() {
        // add current step to displayedForm
        this.displayedForm.currentStep = this.renderedForm.currentStepId;
        // prepare step data for loading the form controls into view
        var currentStep = $.grep(this.renderedForm.stepsDS.data(), $.proxy(this.isCurrentStep, this));
        if (currentStep && currentStep.length === 1) {
            this.prepareStepData(currentStep[0]);
            this.set("currentStep", currentStep[0]);
        }
    },

    /**
     * Whether the step is the current step or not
     * @param step - The step to compare against the current step
     * @returns {boolean}
     */
    isCurrentStep: function(step) {
        return step.id === this.renderedForm.currentStepId;
    },

    setMaxWidth: $.noop,

    prepareStepData: function() {
        // Override if form styles should be ignored (applicable for mobile viewing).
        this._setFormStyles();
    },

    /**
     * Set form styles on rendered form, including border, background, and font styles.
     * Should be called from prepareStepData, if the context wants form styles to be set.
     * @private
     */
    _setFormStyles: function() {
        this.formContainer().setPromptStyles(this.get("renderedForm"));
    },

    /**
     * Should be called from prepareStepData, for mobile viewing.
     * @param currentStep
     * @returns {*}
     * @private
     */
    _prepMobileData: function(currentStep) {
        $.each(currentStep.controlsDS.data(), function(i, val) {
            delete val.x;
            delete val.y;
            delete val.width;
            delete val.height;
            delete val.labelPosition;
        });
    },

    setDirty: function() {
        if (!this.get("readOnly")) {
            this.set("formDirty", true);
        }
    },

    /**
     * Passes through the button which was clicked to perform the submit.
     * @param prompt the submit button, as PromptInputModel data
     */
    onButtonClick: function(prompt) {
        this._submitForm(prompt);
    },

    onEnter() {
        if(!this.loadingForm && !this.renderError) {
            this._submitForm(this.findDefaultSubmitButton());
        }
    },

    /**
     * Finds the default submit button. This first looks for the default button that was configured
     * in the forms designer.  If none was specified, this looks for the "first" enabled,
     * visible button on the form, and uses that.
     *
     * @private
     */
    findDefaultSubmitButton: function() {
        var firstButton,
            defaultButton;

        $.each(this.currentStep.controlsDS.data(), function(i, control) {
            if (control.inputType == PromptInputType.BUTTON && control.disabled !== true && control.hidden !== true) {

                // set the first button for legacy behavior.
                // do not break at this point.
                if (!firstButton) {
                    firstButton = control;
                }

                // if this is the default button, set it, and break out.
                if (control.defaultButton) {
                    defaultButton = control;
                    return false;
                }
            }
        });
        return defaultButton || firstButton;
    },

    /**
     * Detects whether the form is in a submittable state (is there a submit button enabled and visible?)
     * @returns {boolean} True if there is a submit button enabled and visible
     * @private
     */
    _shouldSubmitForm: function(prompt) {
        return !!prompt && !prompt.disabled && !prompt.hidden;
    },

    /**
     * When the CustomPromptList triggers that server validation needs to run, take the data from
     * the CustomPromptList widget, and run form-specific validation.
     *
     * @param e
     * @returns {*}
     */
    handleServerValidation: function(e) {

        this._saveFormData(false);
        var formControlNames = [];
        for (var prop in e.fields) {
            formControlNames.push(prop);
        }

        var promise = formOps.validateForm(this.get("displayedForm"), formControlNames),
            validationPromise = e.promise;

        // disable save in this panel while server validation is ongoing
        this.set("valid", false);

        // hook up validation success/error cases
        promise
            .done(this.ifOp(function() {
                validationPromise.resolve();
            }))
            .handle("error/message/VAL002", this.ifOp(function(err) {
                validationPromise.reject(err);
            }))
            .handle("error/message/VAL003", this.ifOp(function(err) {
                validationPromise.reject(err);
            }));

        return promise;
    },

    /**
     * Gets the data from PromptList controls, and adds it to displayedForm,
     * for saving and submitting the form.
     * @param clickedButton {PromptInputModel} The button that was clicked to submit the form
     * @private
     */
    _saveFormData: function(clickedButton) {
        var me = this,
            value,
            dataType;
        // check if the form data is on the current step
        var isOnCurrentStep = function(existing) {
            return $.inArray(existing.formControlName, me.currentStep.controlsDS.data().map(function(control) {
                return control.name;
            })) > -1;
        };
        // filter out old button because server doesn't detect deleted buttons
        // delete existing data on the current step
        var formData = (this.displayedForm.formData || []).filter(function(existing) {
            return !(existing.dataType === "STRING" && existing.stringValue === "") && isOnCurrentStep(existing) && !!existing.id;
        });
        // get the data that was passed in to be saved on another step (needs the form control id)
        var separateStepFormData = (this.displayedForm.formData || []).filter(function(existing) {
            return !(existing.dataType === "STRING" && existing.stringValue === "") && !isOnCurrentStep(existing) && !existing.id;
        });
        // delete all existing data that exists on the current step
        formData.forEach(function(existing) {
            existing.deleted = true;
        });
        // add all new values
        this.currentStep.controlsDS.data().forEach(function(control) {
            if (!PromptInputType.canHaveInput(control.inputType)) {
                return;
            }
            value = control.value;
            dataType = control.dataType ? control.dataType : "STRING";
            // only save data that has a value
            if (value === undefined || value === null || value === "") {
                me._removeCacheItem(control.name);
                return;
            }
            if (value instanceof kendo.data.ObservableArray || (typeof value === "object" && value != null && "length" in value)) {
                value = $.makeArray(value);
            }
            if (dataType === "STRING_ARRAY" || $.type(value) === "array") {
                value.forEach(function(formDataValue) {
                    formData.push(me._getFormDataItemForSave(dataType, control.id, control.name, formDataValue));
                });
            } else {
                formData.push(me._getFormDataItemForSave(dataType, control.id, control.name, control.value));
            }
        });
        // add the form data that was passed for another step
        separateStepFormData.forEach(function(newData) {
            // get form control id by the control name
            var formControlId;
            for (var i = 0; i < me.renderedForm.stepsDS.data().length; i++) {
                var step = me.renderedForm.stepsDS.at(i);
                for (var j = 0; j < step.controlsDS.data().length; j++) {
                    var control = step.controlsDS.at(j);
                    if (control.name === newData.formControlName) {
                        formControlId = control.id;
                        break;
                    }
                }
                if (formControlId) {
                    break;
                }
            }
            dataType = newData.dataType ? newData.dataType : "STRING";
            value = newData[me._getFormDataValueProperty(dataType)];
            formData.push(me._getFormDataItemForSave(dataType, formControlId, newData.formControlName, value));
        });
        // add the submit button that's relevant to the submission
        if (clickedButton) {
            formData.push(me._getFormDataItemForSave("STRING", clickedButton.id, clickedButton.name, ""));
        }
        // set form data
        this.displayedForm.set("formData", formData);
        // after setting data, handle any files that were uploaded.
        var uploads = this.get("completedUploads");
        if (uploads && uploads.length > 0) {
            this.displayedForm.set("newUploads", uploads);
        }
    },

    _formSaved: function() {
        // remove deleted form data
        if (this.displayedForm.formData) {
            this.displayedForm.formData = this.displayedForm.formData.filter(function(obj) {
                return obj.deleted !== true;
            });
        }
        this._processUploadsAfterSave();
        // Form is not dirty once all changes have been saved
        this.set("formDirty", false);
    },

    /**
     * Clean up pending uploads after a save
     * @private
     */
    _processUploadsAfterSave: function() {
        this.set("completedUploads", []);
        // collect the controls that have new uploads so we can rebuild the prompt
        if (!this.displayedForm.newUploads || !this.displayedForm.newUploads.length) {
            return;
        }
        var newUploads = [];
        this.displayedForm.newUploads.forEach(function(newUpload) {
            if (newUploads.indexOf(newUpload.formControlId) === -1) {
                newUploads.push(newUpload.formControlId);
            }
        });
        this.displayedForm.set("newUploads", []);
        // rebuild file upload prompts for the controls that got new uploads
        if (!this.displayedForm.uploads || !this.displayedForm.uploads.length) {
            return;
        }
        var uploads = {};
        this.displayedForm.uploads.forEach(function(upload) {
            if (newUploads.indexOf(upload.formControlId) === -1) {
                return;
            }
            if (!uploads[upload.formControlId]) {
                uploads[upload.formControlId] = [];
            }
            uploads[upload.formControlId].push(upload);
        });
        this.formContainer().promptWidgets.forEach(function(promptInput) {
            if (newUploads.indexOf(promptInput.options.model.id) >= 0) {
                promptInput.options.model.value = uploads[promptInput.options.model.id];
                this.formContainer().refresh({
                    field: "data",
                    items: [promptInput.options.model]
                });
            }
        }, this);
    },

    _submitting: false,

    _submitForm: function(prompt) {
        if (this.get("_submitting") === true) {
            return $.Deferred().reject().promise();
        }
        this.set("_submitting", true);
        const def = this.pauseUntilStable().then(this.ifPanel(function() {
            if (this._shouldSubmitForm(prompt)) {
                return this._doSubmitForm(prompt);
            }
        })).always(this.ifPanel(function() {
            this.set("_submitting", false);
        }));
        // progress is not defined in the external forms module context, but when it is defined, apply it.
        if (this.progress) {
            this.progress(def);
        }
        return def;
    },

    _handleStale(promise) {
        //This exists so that forms panel can override it.
        return promise;
    },

    /**
     * Submit the form, and run any client side submit actions.
     *
     * @param prompt the submit button, as PromptInputModel data
     * @returns {*}
     * @private
     */
    _doSubmitForm: function(prompt) {
        const me = this;
        // By default, close the form on submit. Certain submit actions can prevent that though.
        let closeOnSubmit = true;
        let isCompleteTask = false;
        this.notify().hide();
        return this.formContainer().validate(prompt.bypassRequiredValidation)
            .then(this.ifPanel(function() {
                this._saveFormData(prompt);
                this.set("successMessage", "");
                this.set("showSuccess", false);
                this.set("errorMessage", "");
                this.clearLastSubmit();
                return this._handleStale(formOps.submitForm(this.get("displayedForm")))
                    .handle("error/message", this.ifPanel(function(data) {
                        // See if there is a Display Feedback Submit Action configured on the clicked
                        // Submit Button, and that it has error text. If so, display it as the error.
                        // Otherwise, use standard error handling of the error from the server.
                        this.trigger("forms/notify/error", {
                            data: data
                        });
                    }));
            }))
            .then(this.ifPanel(function(data) {
                // clean up the temp data so it isn't reused or referenced after a successful submit
                this._formSaved();
                // Set form instance ID if not already set (use case: new form instance)
                if (!this.displayedForm.id) {
                    this.trigger("addForm");
                    this.displayedForm.id = data.id;
                }
                // update formData to have new IDs for controls that were added to the form during the submit
                if (data.formData) {
                    this.displayedForm.formData = this.displayedForm.formData.filter(function(obj) {
                        // remove the items that were deleted from the cached form data so they aren't rendered on a go to step submit action
                        return obj.deleted !== true;
                    }).map(function(obj) {
                        let newDataWithId;
                        for (let i = 0; i < data.formData.length && !newDataWithId; i++) {
                            if (obj.formControlName === data.formData[i].formControlName) {
                                // If ARRAY type, make sure we're saving the id from the correct value.
                                // Only checking ARRAY type should be a lower risk fix, since this is going in late.
                                // At this point, we only support STRING values in arrays, so only checking for STRING_ARRAY too.
                                if (obj.dataType === "STRING_ARRAY" && obj.stringValue !== data.formData[i].stringValue) {
                                    continue;
                                }
                                newDataWithId = data.formData[i];
                            }
                        }
                        return newDataWithId ? newDataWithId : obj;
                    });
                }
                let submitActionsRunning = [];
                // Submit returns the submitActions that need to run on the client side after submit. Run these now.
                if (data.formActions) {
                    $.each(data.formActions, function(i, formAction) {
                        // Add the promise to the list of submit actions to wait on
                        submitActionsRunning.push(me._executeSubmitAction(formAction));
                        // If the submit action prevents close of the form, find out now
                        if (me._doesActionKeepFormOpen(formAction)) {
                            closeOnSubmit = false;
                        }
                        isCompleteTask = isCompleteTask || formAction.type === FormActionType.COMPLETE_TASK;
                    });
                }
                if (this.get("isCompletingTaskForm")) { // Tell jobs to complete form task if we are in the middle of one.
                    this._sendMessageToJobs(true, this.displayedForm.id, true, isCompleteTask);
                }
                // Only triggers if the form is supposed to "close". Certain submit actions can prevent this,
                // such as optional configuration of Go To Step, or error cases of the Load Form submit action.
                return $.when.apply($, submitActionsRunning);
            }))
            .done(this.ifPanel(function() {
                if (closeOnSubmit) {
                    this.set("showSuccess", false);
                    const wasFeedbackDisplayed = Array.prototype.slice.call(arguments).some(function(was) {
                        return was;
                    });
                    if (!wasFeedbackDisplayed) {
                        this._displayDefaultSuccess();
                    }
                    this.trigger("closeForm", {isCompleteTask});
                }
                // triggered on every submit, regardless of whether the form was closed or not
                this.trigger("submitComplete");

                this.set("formDirty", false);
            }));
    },

    clearLastSubmit: function() {

    },

    _displayDefaultSuccess: function() {
        topic.publish("success/message", {
            successKey: "main.forms.submit.success"
        });
    },

    /**
     * Execute an individual submit action. Submit action plugins do not have a client-side piece
     * (as of Unicorn / 10.14), so all submit actions that run on the client are hard-coded here.
     *
     * @param formAction The form action to be executed, including all submit action properties needed
     * @private
     */
    _executeSubmitAction: function(formAction) {
        var me = this;
        switch (formAction.type) {
            case "LOAD_URL":
                return me._executeLoadUrlSubmitAction(formAction);
            case "LOAD_FORM":
                return me._executeLoadFormSubmitAction(formAction);
            case "GO_TO_STEP":
                return me._executeGoToStepSubmitAction();
            case "DISPLAY_FEEDBACK":
                return me._executeDisplayFeedbackSubmitAction(formAction);
            default:
                return $.Deferred().resolve().promise();
        }
    },

    /**
     * Figure out whether the action should keep the form open.  Right now the only ones that do this are go to step and load form.
     * @param formAction the form action
     * @private
     */
    _doesActionKeepFormOpen: function(formAction) {
        return formAction.type === "LOAD_FORM" || formAction.type === "GO_TO_STEP";
    },

    /**
     * This is handled differently in Panel and External Forms, so dispatch a message
     * here that each will handle in its own way.
     *
     * @param formAction the submit action with properties to execute the action
     * @returns {*}
     * @private
     */
    _executeLoadUrlSubmitAction: function(formAction) {
        var url = formAction.url;
        if (url && url.indexOf("://") === -1) {
            url = "//" + url;
        }
        this.trigger("open/window", url);
        return $.Deferred().resolve().promise();
    },

    /**
     * Load the form specified by the Submit Action, stored in a property named "formName".
     * Loading the next form should always load in Edit Mode, as it's always a new instance
     * of a form design, and only saved instances can be opened in VIEW mode.
     *
     * @param formAction the submit action with properties to execute the action
     * @returns {*}
     * @private
     */
    _executeLoadFormSubmitAction: function(formAction) {
        var formName = formAction.formName;
        var form = new SavedFormModel();
        form.formName = formName;
        form.formData = this.get("displayedForm").get("formData");
        return this._editForm(form);
    },

    /**
     * The server handled getting this form instance into the correct step.
     * All we have to do now is reload it, if we are not closing the form.
     * There is a property with name "step" that says which step we are going to,
     * but the client doesn't actually care.
     * If this was a new form, we haven't stored the id and don't want to yet. It's the
     * only way we have of knowing it's a new form when returning to the library.
     *
     * @returns {*}
     * @private
     */
    _executeGoToStepSubmitAction: function() {
        var me = this;
        return this._openForm(this.get("displayedForm"), this.get("readOnly") ? "VIEW" : "EDIT")
            .done(this.ifOp(function() {
                me.openComplete(me.get("readOnly"));
            }));
    },

    /**
     * Handle displaying a custom, user-entered success message, INSTEAD OF our default message for
     * successful form submission.
     *
     * @param formAction the submit action with properties to execute the action
     * @returns {*}
     * @private
     */
    _executeDisplayFeedbackSubmitAction: function(formAction) {
        var def = $.Deferred();
        // successMessage is a required property of this submit action's configuration.
        var msg = formAction.successMessage;
        this.displaySuccessFeedback(msg);
        this.set("showSuccess", true);
        return def.resolve(true).promise();
    },

    /**
     * Notify the app to display feedback
     */
    displaySuccessFeedback: function(message) {
        topic.publish("form/success/message", {
            customMessage: message
        });
    },

    onErrorLinkClick: function() {
        this.trigger("renderErrorClick");
    },

    /**
     * Returns a single promise for all the uploads that occurred during the form edit
     * @returns {*}
     */
    uploadProgress: function() {
        var me = this;
        var defs = $.map(this.pendingUploads, function(pending) {
            return pending;
        });
        return $.when.apply($, defs);
    },

    /**
     * Return a promise that won't resolve until all file uploads have been handled and all dependencies have been executed.
     */
    pauseUntilStable: function() {
        var me = this;
        return me.uploadProgress()
            .then(function() {
                return me.formContainer().getFinished();
            });
    },

    /**
     * Cancel in-progress uploads
     */
    cancelUploads: function() {
        // cancel the uploads by clicking the cancel button on the file entry
        this.formContainer().element
            .find("[data-role='fileuploadpromptinput']")
            .find(".k-upload-action>.k-i-cancel")
            .click();
    },

    /**
     * Remove all the unsaved file uploads
     * @returns {promise} Promise for all the remove requests
     * @private
     */
    _removeTempUploads: function() {
        var defs = [];
        this.completedUploads.forEach(function(item) {
            defs.push(formOps.removeTempUpload(item.formTempUploadId));
        });
        return $.when.apply($, defs);
    },

    /**
     * The user has started a file upload operation.  We need to track the total upload ops here,
     * since there could be multiple upload controls on a form at one time, we can't rely on the widget to do it.
     * @param e
     */
    handleUploadStarted: function(e) {
        // set dirty up front in case user closes panel while uploads are in progress
        this.setDirty();
        this.pendingUploads[e.file.uid] = $.Deferred();
    },

    handleUploadError: function(e) {
        this.pendingUploads[e.file.uid].reject();
        delete this.pendingUploads[e.file.uid];
    },

    handleUploadSuccess: function(e) {
        // we will get back a FormTempUploadModel
        // we need to pass that to the save method so the server knows to move the image to the saved state.
        // handle users removing files after they have been uploaded.  Removed from completedUploads, see below.
        this.get("completedUploads").push(new FormNewFileUploadModel({
            formTempUploadId: e.response.id,
            formControlId: e.prompt.id,
            fileUid: e.file.uid
        }));

        this.pendingUploads[e.file.uid].resolve();
    },

    /**
     * Event handler for a click on the file upload anchor
     * @param e {{ file: {} }}
     */
    handleViewUpload: function(e) {
        // override to impl view handler
    },

    /**
     * The user wants to remove a file they have previously uploaded.
     *
     * @param e object containing the file to remove and a promise to resolve
     */
    handleRemoveUpload: function(e) {
        this.pendingUploads[e.file.uid] = e.promise;
        const completedItems = this.get("completedUploads");
        const uploads = this.displayedForm?.uploads || [];
        const completedUploadIndex = [].findIndex.call(completedItems, item => item.fileUid === e.file.uid);
        const upload = [].find.call(uploads, up => up.id === e.file.id);
        if (completedUploadIndex !== -1) {
            // remove the item from the completedUploads, so we don't try to save it when the form instance is saved.
            const [tempItem] = completedItems.splice(completedUploadIndex, 1);
            // since the uploaded file has not been saved, we need to tell the server to remove temp file
            formOps.removeTempUpload(tempItem.formTempUploadId)
                .done(function() {
                    // resolve the promise to signal the widget to remove the line item for this file
                    e.promise.resolve();
                }).fail(() => {
                    e.promise.reject();
                    delete this.pendingUploads[e.file.uid];
                });
        } else if(upload) {
            this.confirmRemove(e).done(() => {
                upload.set("markedForDelete", true);
                this.setDirty();
                e.promise.resolve();
            }).fail(() => {
                e.promise.reject();
                delete this.pendingUploads[e.file.uid];
            });
        } else {
            e.promise.resolve();
        }
    },

    confirmRemove: function(e) {
        return $.when(true);
    }

}, OperationViewModelMixin);
